import { ChevronDown } from "lucide-react";
import {
  Control,
  Controller,
  FieldErrors,
  Path,
  RegisterOptions,
} from "react-hook-form";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";

interface Option {
  label: string;
  value: string | number;
}

interface FormSelectProps<T extends Record<string, any>> {
  name: Path<T>;
  label: string;
  placeholder: string;
  options: Option[];
  control: Control<T>;
  rules?: RegisterOptions<T>;
  errors: FieldErrors<T>;
}

const FormSelect = <T extends Record<string, any>>(
  props: FormSelectProps<T>
) => {
  const { control, name, errors, rules, label, placeholder, options } = props;

  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      render={({ field }) => {
        const selected = options.find((option) => option.value === field.value);
        const error = name
          .split(".")
          .reduce<any>((acc, key) => acc?.[key], errors);

        return (
          <div className="flex flex-col w-full gap-1.5">
            {label && (
              <label htmlFor={name} className="font-medium text-xs">
                {label}
              </label>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center justify-between rounded-lg border px-3 h-[42px] text-xs font-medium outline-none w-full bg-white">
                <span
                  className={selected ? "text-charcoal" : "text-light-black"}
                >
                  {selected ? selected.label : placeholder}
                </span>
                <ChevronDown className="h-4 w-4 text-light-black shrink-0" />
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                className="bg-white w-[var(--radix-dropdown-menu-trigger-width)] max-h-60 overflow-y-auto outline-none"
              >
                {options.map((option) => {
                  const isSelected = option.value === field.value;
                  return (
                    <DropdownMenuItem
                      key={option.value}
                      onSelect={() => field.onChange(option.value)}
                      className="flex items-center gap-3 text-xs font-medium outline-none hover:cursor-pointer hover:bg-gray-50 py-2"
                    >
                      <div className="flex w-4 h-4 rounded-full border border-gray-400 shrink-0">
                        {isSelected ? (
                          <div className="w-2.5 h-2.5 bg-primary rounded-full m-auto" />
                        ) : null}
                      </div>
                      {option.label}
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
            {typeof error?.message === "string" && (
              <span className="text-red-500 text-xs font-medium" role="alert">
                {error.message}
              </span>
            )}
          </div>
        );
      }}
    />
  );
};

export default FormSelect;
